// ==========================================
// AttendanceModule.jsx (Corrected & Optimized for Mobile Passkeys)
// ==========================================

import React, { useState } from "react";
import { FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaFingerprint } from "react-icons/fa";
import { apiService } from "../services/apiService";

const AttendanceModule = ({ onBack, initialBiometricStatus }) => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [attendanceRecord, setAttendanceRecord] = useState(null);

  // Fallback: Check incoming prop first, then check sessionStorage immediately
  const [isRegistered, setIsRegistered] = useState(() => {
    if (initialBiometricStatus !== undefined && initialBiometricStatus !== null) {
      return initialBiometricStatus;
    }
    return apiService.getBiometricStatus ? apiService.getBiometricStatus() : false;
  });

  const [showThumbModal, setShowThumbModal] = useState(false);
  const [thumbCaptured, setThumbCaptured] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [modalActionType, setModalActionType] = useState(""); // 'register', 'checkIn', 'checkOut'
  const [autoOutParam, setAutoOutParam] = useState(false);

  // Helper to retrieve browser geolocation coordinates
  const fetchCoordinates = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser."));
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          () => {
            reject(new Error("Unable to retrieve your location. Please ensure location/GPS permissions are enabled."));
          },
          { enableHighAccuracy: true }
        );
      }
    });
  };

  // Open Popup Modal for Biometric Scanning
  const openThumbModal = (actionType, isAuto = false) => {
    setErrorMessage("");
    setSuccessMessage("");
    setModalActionType(actionType);
    setAutoOutParam(isAuto);
    setThumbCaptured(false);
    setIsScanning(false);
    setShowThumbModal(true);
  };

  // Real WebAuthn Browser Biometric Prompt (Touch ID, Windows Hello, Hardware Key, or Passkey Creation/Login)
  const handleScanThumbAction = async () => {
    setErrorMessage("");
    setIsScanning(true);

    try {
      if (!window.PublicKeyCredential) {
        throw new Error("Biometric authentication (WebAuthn) is not supported by this browser.");
      }

      // Generate random challenge for secure credential exchange
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      if (modalActionType === "register") {
        // --- PASSKEY REGISTRATION FLOW (Creates and saves a passkey to mobile enclave) ---
        const userId = new Uint8Array(16);
        window.crypto.getRandomValues(userId);

        const publicKeyCredentialCreationOptions = {
          challenge: challenge,
          rp: {
            name: "Shivaba Attendance Portal",
            id: window.location.hostname, // Binds strictly to your domain
          },
          user: {
            id: userId,
            name: "employee@shivaba.in",
            displayName: "Employee User",
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },  // ES256
            { alg: -257, type: "public-key" } // RS256
          ],
          timeout: 60000,
          attestation: "direct",
          authenticatorSelection: {
            authenticatorAttachment: "platform", // Forces built-in mobile biometrics (FaceID/TouchID/Fingerprint)
            userVerification: "required",
            residentKey: "preferred"
          }
        };

        const credential = await navigator.credentials.create({
          publicKey: publicKeyCredentialCreationOptions,
        });

        if (credential) {
          setThumbCaptured(true);
          setSuccessMessage("Passkey created and saved on mobile device successfully!");
        }

      } else {
        // --- PASSKEY AUTHENTICATION / CHECK-IN / CHECK-OUT FLOW ---
        const publicKeyCredentialRequestOptions = {
          challenge: challenge,
          timeout: 60000,
          userVerification: "required", // Forces device hardware biometric prompt
        };

        const credential = await navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions,
        });

        if (credential) {
          setThumbCaptured(true);
          setSuccessMessage("Device biometric verified successfully! You can now submit.");
        }
      }

    } catch (err) {
      setErrorMessage("Biometric operation cancelled or failed: " + err.message);
    } finally {
      setIsScanning(false);
    }
  };

  // Handle final submission once biometric prompt succeeds
  const handleModalSubmit = async () => {
    if (!thumbCaptured) {
      setErrorMessage("Please complete biometric verification first.");
      return;
    }

    setShowThumbModal(false);
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (modalActionType === "register") {
        const payload = { template: `webauthn_passkey_${Date.now()}` };
        await apiService.attendance.registerBiometric(payload);
        setIsRegistered(true);
        setSuccessMessage("Biometric registered successfully! Option locked.");
      } 
      else if (modalActionType === "checkIn") {
        const coords = await fetchCoordinates();
        const payload = { latitude: coords.latitude, longitude: coords.longitude, biometricVerified: true };
        const response = await apiService.attendance.checkIn(payload);
        setAttendanceRecord(response);
        setSuccessMessage(`Biometric Check-In successful at ${new Date(response.checkInTime).toLocaleTimeString()}`);
      } 
      else if (modalActionType === "checkOut") {
        let lat = 0.0;
        let lon = 0.0;
        if (!autoOutParam) {
          const coords = await fetchCoordinates();
          lat = coords.latitude;
          lon = coords.longitude;
        }
        const payload = { latitude: lat, longitude: lon, isAuto: autoOutParam, biometricVerified: true };
        const response = await apiService.attendance.checkOut(payload);
        setAttendanceRecord(response);
        setSuccessMessage(`Biometric Check-Out successful at ${new Date(response.checkOutTime).toLocaleTimeString()}`);
      }
    } catch (err) {
      setErrorMessage(err.message || "An unexpected error occurred during processing.");
    } finally {
      setLoading(false);
      setThumbCaptured(false);
    }
  };

  return (
    <div className="attendance-tracker-container">
      {/* Header Bar */}
      <div className="attendance-header">
        <div className="header-left">
          {onBack && (
            <button className="back-btn" onClick={onBack}>
              <FaArrowLeft /> Back
            </button>
          )}
          <h2>
            <FaMapMarkerAlt className="header-icon" /> Geo-Coordinate & Secure Biometric Attendance
          </h2>
        </div>
      </div>

      {/* Control Panel */}
      <div className="attendance-controls">
        
        {/* Biometric Registration Box */}
        <div className="biometric-registration-section" style={{ background: "#f8f9fa", padding: "15px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "15px" }}>
          <h4 style={{ margin: "0 0 10px 0", color: "#1a237e", display: "flex", alignItems: "center", gap: "8px", fontSize: "15px" }}>
            <FaFingerprint /> Device Biometric Registration
          </h4>
          
          {!isRegistered ? (
            <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-save-attendance"
                onClick={() => openThumbModal("register")}
                disabled={loading}
                style={{ backgroundColor: "#3182ce", flex: "1", minWidth: "220px", justifyContent: "center" }}
              >
                <FaFingerprint style={{ marginRight: "6px" }} /> Register Biometric (OS Prompt)
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#38a169", fontWeight: "600", fontSize: "14px" }}>
              <FaCheckCircle /> Biometric Successfully Registered & Option Locked!
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="control-group" style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "10px", flexWrap: "wrap" }}>
          {isRegistered && (
            <>
              <button
                className="btn-save-attendance"
                onClick={() => openThumbModal("checkIn")}
                disabled={loading}
                style={{ flex: 1, backgroundColor: "#276749", justifyContent: "center", minWidth: "130px" }}
              >
                <FaFingerprint style={{ marginRight: "6px" }} /> {loading ? "Processing..." : "Biometric Check-In"}
              </button>

              <button
                className="btn-save-attendance"
                onClick={() => openThumbModal("checkOut", false)}
                disabled={loading}
                style={{ flex: 1, backgroundColor: "#9b2c2c", justifyContent: "center", minWidth: "130px" }}
              >
                <FaFingerprint style={{ marginRight: "6px" }} /> {loading ? "Processing..." : "Biometric Check-Out"}
              </button>

              <button
                className="btn-save-attendance"
                onClick={() => openThumbModal("checkOut", true)}
                disabled={loading}
                style={{ flex: 1, backgroundColor: "#c05621", justifyContent: "center", minWidth: "130px" }}
                title="Triggers default closing out-time adjustment via biometrics if missed"
              >
                <FaFingerprint style={{ marginRight: "6px" }} /> {loading ? "Processing..." : "Auto Biometric Out"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Popup Modal for Triggering WebAuthn */}
      {showThumbModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex", justifyContent: "center",
          alignItems: "center", zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "#fff", padding: "30px", borderRadius: "12px", width: "380px",
            textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }}>
            <h3 style={{ margin: "0 0 10px 0", color: "#1a237e" }}>Secure Device Biometrics</h3>
            <p style={{ fontSize: "14px", color: "#64748b", marginBottom: "20px" }}>
              {modalActionType === "register" 
                ? "Click below to create and save a passkey on this mobile device." 
                : "Click below to prompt your device scanner (Touch ID, Face ID, or PIN)."}
            </p>

            {/* Scanner Trigger Pad */}
            <div 
              onClick={!isScanning ? handleScanThumbAction : undefined}
              style={{
                width: "120px", height: "120px", margin: "0 auto 20px auto", borderRadius: "50%",
                backgroundColor: thumbCaptured ? "#c6f6d5" : "#edf2f7", border: `3px dashed ${thumbCaptured ? "#38a169" : "#cbd5e0"}`,
                display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                cursor: isScanning ? "wait" : "pointer", transition: "all 0.3s ease"
              }}
              title="Click to trigger OS biometric check"
            >
              <FaFingerprint size={50} color={thumbCaptured ? "#38a169" : isScanning ? "#3182ce" : "#a0aec0"} />
              <span style={{ fontSize: "11px", fontWeight: "600", marginTop: "6px", color: thumbCaptured ? "#276749" : "#718096" }}>
                {isScanning ? "Waiting..." : thumbCaptured ? "Verified" : "Tap to Scan"}
              </span>
            </div>

            {thumbCaptured && (
              <p style={{ color: "#38a169", fontSize: "13px", fontWeight: "600", marginBottom: "15px" }}>
                ✓ Device verification confirmed!
              </p>
            )}

            {/* Modal Action Buttons */}
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => setShowThumbModal(false)}
                style={{
                  padding: "10px 20px", backgroundColor: "#e2e8f0", border: "none",
                  borderRadius: "6px", fontWeight: "600", cursor: "pointer", color: "#4a5568"
                }}
              >
                Cancel
              </button>
              
              <button
                type="button"
                onClick={handleModalSubmit}
                disabled={!thumbCaptured}
                style={{
                  padding: "10px 20px", backgroundColor: !thumbCaptured ? "#cbd5e0" : "#2b6cb0",
                  border: "none", borderRadius: "6px", fontWeight: "600", cursor: thumbCaptured ? "pointer" : "not-allowed",
                  color: "#fff"
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Banners */}
      {successMessage && (
        <div className="status-banner success">
          <FaCheckCircle className="banner-icon" />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="status-banner error">
          <FaTimesCircle className="banner-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Summary Table Card Display */}
      <div className="attendance-table-card">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Record ID</th>
              <th>User ID</th>
              <th>Date</th>
              <th>Check-In Time</th>
              <th>Check-Out Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {attendanceRecord ? (
              <tr>
                <td className="roll-no">#{attendanceRecord.id}</td>
                <td className="student-name">{attendanceRecord.userId}</td>
                <td>{attendanceRecord.attendanceDate}</td>
                <td>
                  {attendanceRecord.checkInTime
                    ? new Date(attendanceRecord.checkInTime).toLocaleTimeString()
                    : "-"}
                </td>
                <td>
                  {attendanceRecord.checkOutTime
                    ? new Date(attendanceRecord.checkOutTime).toLocaleTimeString()
                    : "-"}
                </td>
                <td>
                  <span
                    className={`badge ${
                      attendanceRecord.status === "PRESENT" ? "present" : "late"
                    }`}
                  >
                    {attendanceRecord.status}
                  </span>
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", color: "#718096", padding: "30px" }}>
                  No biometric attendance recorded yet for today. Complete registration and use the controls above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceModule;